import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Image, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useHighlightOverrideQuery } from '@/client/api/highlights/highlights.query';
import {
  useDeleteHighlightOverrideMutation,
  useUpsertHighlightOverrideMutation,
} from '@/client/api/highlights/highlights.mutation';

interface Props {
  etid: number;
}

const EMPTY = { title: '', subtitle: '', cover_asid: '', deeplink: '' };

/** Per-etid marketing copy + cover override for a highlight card. */
export function HighlightOverridePanel({ etid }: Props) {
  const { data: override, isLoading } = useHighlightOverrideQuery(etid);
  const upsert = useUpsertHighlightOverrideMutation();
  const del = useDeleteHighlightOverrideMutation();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    setForm({
      title: override?.title ?? '',
      subtitle: override?.subtitle ?? '',
      cover_asid: override?.cover_asid != null ? String(override.cover_asid) : '',
      deeplink: override?.deeplink ?? '',
    });
  }, [override, etid]);

  const handleSave = async () => {
    if (form.cover_asid.trim() && Number.isNaN(Number(form.cover_asid))) {
      toast.error('Cover asid must be a number');
      return;
    }
    try {
      await upsert.mutateAsync({
        etid,
        title: form.title.trim() || null,
        subtitle: form.subtitle.trim() || null,
        cover_asid: form.cover_asid.trim() ? Number(form.cover_asid) : null,
        deeplink: form.deeplink.trim() || null,
      });
      toast.success('Override saved');
    } catch (e: any) {
      toast.error(e?.message ?? 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Reset this card back to its default name and cover?')) return;
    try {
      await del.mutateAsync(etid);
      setForm(EMPTY);
      toast.success('Reset to default');
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not reset');
    }
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Image className="h-4 w-4 text-blue-700" />
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Heading &amp; cover</h2>
            <p className="text-xs text-gray-500">The title, tagline and image shown on the card.</p>
          </div>
        </div>
        {override && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={handleDelete} disabled={del.isPending}>
            <Trash2 className="mr-1 h-4 w-4" />
            Reset
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Heading</Label>
            <Input
              value={form.title}
              placeholder="Leave blank to use the category name"
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Tagline</Label>
            <Input
              value={form.subtitle}
              placeholder="A short line under the heading (optional)"
              onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Links to</Label>
              <Input
                value={form.deeplink}
                placeholder="/market/phones"
                onChange={(e) => setForm((p) => ({ ...p, deeplink: e.target.value }))}
              />
              <p className="text-[11px] text-gray-400">Where tapping the card takes the shopper.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Cover image ID</Label>
              <Input
                type="number"
                value={form.cover_asid}
                placeholder="Optional"
                onChange={(e) => setForm((p) => ({ ...p, cover_asid: e.target.value }))}
              />
              <p className="text-[11px] text-gray-400">Asset ID of the background image.</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={upsert.isPending} className="gap-2">
            <Save className="h-4 w-4" />
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}
